import React, { useEffect, useState } from "react";

import { css, useTheme } from "@emotion/react";
import classNames from "classnames";

import useComponentVisible from "../../../../hooks/useComponentVisible";
import { Permission, useHoyerAcl } from "../../../../hooks/useHoyerAcl";
import {
    useIsAuthenticated,
    useIsBetaTester,
    useKeycloakOpt,
} from "../../../../providers/AuthProvider";
import PredefinedIcon, {
    PredefinedIconType,
} from "../../icons/Icon/Predefined";
import LinkComponent from "../../LinkComponent";
import SubNav from "../Desktop/LinkItems/SubNav";
import {
    LinkItemClickable,
    StyledIconContainer,
    SubNavContainer,
} from "../Desktop/TopRow/TopLinkItems";
import useNavigationStore from "../store";

/**
 * Returns an array to create the account navigation.
 */
export const GetAccountSection = () => {
    const { can, canAny } = useHoyerAcl();
    const isAuthenticated = useIsAuthenticated();
    const isBetaTester = useIsBetaTester();

    if (!isAuthenticated) return;

    const hoyerCardAccountLinks = [
        { href: "/hoyercard-portal", text: "Übersicht", permission: null },
        {
            href: "/hoyercard-portal/activation",
            text: <>Kundennummer&nbsp;aktivieren</>,
            permission: Permission.CUSTOMER_ACTIVATE_CUSTOMER,
        },
        {
            href: "/hoyercard-portal/admin",
            text: "Admin-Bereich",
            anyPermissions: [
                Permission.USER_CONNECT_TO_CUSTOMER,
                Permission.USER_GENERATE_ACTIVATION_CODE,
                Permission.USER_DISCONNECT_FROM_CUSTOMER,
            ],
        },
    ];

    const hoyerCardAccountV2Links = [
        { href: "/hoyercard-portal/v2", text: "Übersicht", permission: null },
        {
            href: "/hoyercard-portal/v2/activation",
            text: <>Geschäftspartnernummer&nbsp;aktivieren</>,
            permission: Permission.CUSTOMER_ACTIVATE_CUSTOMER,
        },
        {
            href: "/hoyercard-portal/v2/admin",
            text: "Admin-Bereich",
            anyPermissions: [
                Permission.USER_CONNECT_TO_CUSTOMER,
                Permission.USER_GENERATE_ACTIVATION_CODE,
                Permission.USER_DISCONNECT_FROM_CUSTOMER,
            ],
        },
    ];

    const callForTenderAccountLinks = [
        { href: "/ausschreibungsportal", text: "Übersicht" },
        {
            href: "/ausschreibungsportal/profil",
            text: "Ausschreibungsprofil",
        },
        {
            href: "/ausschreibungsportal/admin",
            text: "Admin-Bereich",
            permission: Permission.AUSSCHREIBUNG_MANAGE,
        },
    ];

    const isAllowed = (link) => {
        if (!link.permission && !link.anyPermissions) {
            return true;
        }
        if (link.permission) {
            return can(link.permission);
        }
        if (link.anyPermissions) {
            return canAny(link.anyPermissions);
        }
        return false;
    };

    return {
        name: "Mein Konto",
        subsections: [
            ...(isBetaTester
                ? [
                      {
                          name: "Hoyer Card Portal V2",
                          items: hoyerCardAccountV2Links.filter(isAllowed),
                      },
                  ]
                : []),
            {
                name: "Hoyer Card Portal",
                items: hoyerCardAccountLinks.filter(isAllowed),
            },
            {
                name: "Ausschreibungsportal",
                items: callForTenderAccountLinks.filter(isAllowed),
            },
        ],
    };
};

const HoyerCard = () => {
    const { ref, isVisible, setIsVisible } = useComponentVisible(false);
    const isAuthenticated = useIsAuthenticated();

    const accountSection = GetAccountSection();
    const [path] = useNavigationStore((state) => [state.path]);
    if (!isAuthenticated) return null;

    return (
        <SubNavContainer
            ref={ref}
            className={classNames({
                active: isVisible,
            })}
            onClick={() => setIsVisible(!isVisible)}
        >
            <LinkItemClickable
                active={
                    path &&
                    (path.includes("hoyercard-portal") ||
                        path.includes("ausschreibungsportal"))
                }
            >
                <StyledIconContainer>
                    <PredefinedIcon
                        type={PredefinedIconType.user}
                        color="#05235f"
                    />
                </StyledIconContainer>
                <span>{accountSection.name}</span>
            </LinkItemClickable>
            <SubNav subsections={accountSection.subsections} isOpen={isVisible}>
                <LogoutSection />
            </SubNav>
        </SubNavContainer>
    );
};

export const LogoutSection = () => {
    const { authenticated, email } = useKeycloakOpt();
    const theme = useTheme();

    const containerStyles = css`
        color: ${theme.colors.blue.toString()};
        display: flex;
        gap: 30px;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        padding-top: 20px;
        margin-top: 20px;
        border-top: 1px solid ${theme.colors.grey.toString()};
    `;

    if (!authenticated) {
        return null;
    }

    return (
        <div css={containerStyles}>
            <span>{email}</span>
            <LinkComponent button link={{ text: "Logout", href: "/logout" }} />
        </div>
    );
};

export default HoyerCard;
